import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Briefcase,
  Phone,
  MapPin,
  FileText,
  Loader2,
  X,
  Check,
  ChevronDown,
  ChevronRight,
  ArrowLeft,
  DollarSign,
  AlertTriangle,
  GitMerge
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const JOB_TYPES = ['Cuttings', 'Shavings', 'Other'];

// Default rate templates
const RATE_TEMPLATES = [
  { name: 'Cuttings', uom: 'Tonne', rate: 0 },
  { name: 'Shavings', uom: 'Tonne', rate: 0 },
  { name: 'Chain Up', uom: 'Each', rate: 0 },
  { name: 'Poly', uom: 'Each', rate: 0 },
];

export default function JobsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isAdminOrManager = isAdmin || user?.role === 'manager';
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);  // existing clients for dropdown
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedJob, setExpandedJob] = useState(null);

  // Duplicate-jobs admin dialog state
  const [showDupesDialog, setShowDupesDialog] = useState(false);
  const [dupes, setDupes] = useState([]);
  const [dupesLoading, setDupesLoading] = useState(false);
  const [mergeBusyKey, setMergeBusyKey] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    job_number: '',
    client_name: '',
    contact_name: '',
    contact_phone: '',
    approval_email: '',
    billing_address: '',
    site_name: '',
    location: '',
    directions: '',
    instructions: '',
    job_types: [],
    active: true,
    rate_sheets: []
  });

  useEffect(() => {
    fetchJobs();
    // Fetch clients for the new-job dropdown
    const token = localStorage.getItem('vaultai_token');
    axios.get(`${API}/clients`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setClients(res.data?.clients || res.data || []))
      .catch(() => setClients([]));
  }, []);

  const fetchJobs = async () => {
    const token = localStorage.getItem('vaultai_token');
    try {
      const response = await axios.get(`${API}/dispatch/jobs?active_only=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        // Group jobs by job_number to display as single rows
        // (Some jobs like #689 have separate DB docs for cuttings/shavings)
        const rawJobs = response.data.jobs || [];
        const grouped = {};
        
        rawJobs.forEach(job => {
          const key = job.job_number;
          if (!grouped[key]) {
            grouped[key] = { ...job, job_types: [...(job.job_types || [])] };
          } else {
            // Merge job_types from duplicate job_number entries
            const existingTypes = grouped[key].job_types || [];
            const newTypes = job.job_types || [];
            newTypes.forEach(t => {
              if (!existingTypes.includes(t)) {
                existingTypes.push(t);
              }
            });
            grouped[key].job_types = existingTypes;
          }
        });
        
        setJobs(Object.values(grouped));
      }
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem('vaultai_token');
    setSaving(true);
    
    try {
      if (editingJob) {
        await axios.put(`${API}/dispatch/jobs/${editingJob.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/dispatch/jobs`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setShowDialog(false);
      setEditingJob(null);
      resetForm();
      fetchJobs();
    } catch (error) {
      console.error('Failed to save job:', error);
      alert('Failed to save job');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (job) => {
    const token = localStorage.getItem('vaultai_token');
    try {
      await axios.delete(`${API}/dispatch/jobs/${job.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchJobs();
    } catch (error) {
      console.error('Failed to delete job:', error);
    }
  };

  const openEdit = (job) => {
    setEditingJob(job);
    setFormData({
      job_number: job.job_number || '',
      client_name: job.client_name || '',
      contact_name: job.contact_name || '',
      contact_phone: job.contact_phone || '',
      approval_email: job.approval_email || '',
      billing_address: job.billing_address || '',
      site_name: job.site_name || '',
      location: job.location || '',
      directions: job.directions || '',
      instructions: job.instructions || '',
      job_types: job.job_types || [],
      active: job.active !== false,
      rate_sheets: job.rate_sheets || []
    });
    setShowDialog(true);
  };

  const openNew = () => {
    setEditingJob(null);
    resetForm();
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      job_number: '',
      client_name: '',
      contact_name: '',
      contact_phone: '',
      approval_email: '',
      billing_address: '',
      site_name: '',
      location: '',
      directions: '',
      instructions: '',
      job_types: [],
      active: true,
      rate_sheets: []
    });
  };

  // Rate sheet management
  const addDefaultRates = () => {
    setFormData(prev => ({
      ...prev,
      rate_sheets: [...RATE_TEMPLATES]
    }));
  };

  const updateRate = (index, field, value) => {
    setFormData(prev => {
      const newRates = [...prev.rate_sheets];
      newRates[index] = { ...newRates[index], [field]: field === 'rate' ? parseFloat(value) || 0 : value };
      return { ...prev, rate_sheets: newRates };
    });
  };

  const addRate = () => {
    setFormData(prev => ({
      ...prev,
      rate_sheets: [...prev.rate_sheets, { name: '', uom: 'Each', rate: 0 }]
    }));
  };

  const removeRate = (index) => {
    setFormData(prev => ({
      ...prev,
      rate_sheets: prev.rate_sheets.filter((_, i) => i !== index)
    }));
  };

  const toggleJobType = (type) => {
    const lower = type.toLowerCase();
    setFormData(prev => ({
      ...prev,
      job_types: prev.job_types.includes(lower)
        ? prev.job_types.filter(t => t !== lower)
        : [...prev.job_types, lower]
    }));
  };

  const filteredJobs = jobs.filter(job => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      job.job_number?.toLowerCase().includes(search) ||
      job.client_name?.toLowerCase().includes(search) ||
      job.contact_name?.toLowerCase().includes(search) ||
      job.site_name?.toLowerCase().includes(search)
    );
  });

  // ===== Duplicate jobs admin helpers =====
  const openDupesDialog = async () => {
    setShowDupesDialog(true);
    setDupesLoading(true);
    try {
      const token = localStorage.getItem('vaultai_token');
      const res = await axios.get(`${API}/dispatch/admin/duplicate-jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.success) {
        setDupes(res.data.duplicates || []);
      } else {
        toast.error(res.data?.error || 'Failed to load duplicates');
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to load duplicates');
    } finally {
      setDupesLoading(false);
    }
  };

  const mergeDuplicateGroup = async (jobNumber, keepId, deleteIds) => {
    if (!isAdmin) {
      toast.error('Admin only');
      return;
    }
    const ok = window.confirm(
      `Merge job #${jobNumber}: keep 1 record and delete ${deleteIds.length} duplicate(s)?\n\n` +
      `All assignments and client tickets pointing at the duplicate(s) will be re-pointed at the kept record. This cannot be undone.`
    );
    if (!ok) return;
    const key = `${jobNumber}:${keepId}`;
    setMergeBusyKey(key);
    try {
      const token = localStorage.getItem('vaultai_token');
      const res = await axios.post(
        `${API}/dispatch/admin/jobs/merge`,
        { keep_id: keepId, delete_ids: deleteIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success) {
        toast.success(
          `Merged job #${jobNumber}: ` +
          `${res.data.assignments_repointed} assignment(s), ` +
          `${res.data.tickets_repointed} ticket(s) re-pointed`
        );
        // Refresh both the dupes list and the main jobs list
        await openDupesDialog();
        fetchJobs();
      } else {
        toast.error(res.data?.error || 'Merge failed');
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || err.response?.data?.error || 'Merge failed');
    } finally {
      setMergeBusyKey(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="jobs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/dashboard/dispatch">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Briefcase className="h-6 w-6 text-blue-600" />
              Job Database
            </h1>
            <p className="text-slate-500 text-sm">Manage jobs, clients, and site details</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAdminOrManager && (
            <Button
              variant="outline"
              onClick={openDupesDialog}
              data-testid="find-duplicate-jobs-btn"
            >
              <AlertTriangle className="h-4 w-4 mr-2 text-amber-500" />
              Find Duplicates
            </Button>
          )}
          <Button onClick={openNew} data-testid="add-job-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Job
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search jobs..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="jobs-search"
        />
      </div>

      {/* Jobs List */}
      <div className="grid gap-3">
        {filteredJobs.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-slate-500">
              {searchTerm ? 'No jobs match your search' : 'No jobs yet. Click "Add Job" to create one.'}
            </CardContent>
          </Card>
        ) : (
          filteredJobs.map((job) => (
            <Card 
              key={job.id} 
              className={`transition-all ${!job.active ? 'opacity-50' : ''} ${expandedJob === job.id ? 'ring-2 ring-blue-500' : ''}`}
              data-testid={`job-card-${job.job_number}`}
            >
              <CardContent className="p-4">
                {/* Job Header */}
                <div className="flex items-center justify-between">
                  <div 
                    className="flex items-center gap-3 flex-1 cursor-pointer"
                    onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                  >
                    {expandedJob === job.id ? (
                      <ChevronDown className="h-5 w-5 text-slate-400" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-slate-400" />
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-blue-600">#{job.job_number}</span>
                        <span className="font-semibold">{job.client_name}</span>
                        {!job.active && <Badge variant="secondary">Inactive</Badge>}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                        {job.contact_name && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {job.contact_name} {job.contact_phone && `(${job.contact_phone})`}
                          </span>
                        )}
                        {job.site_name && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {job.site_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {job.job_types?.map(type => (
                      <Badge key={type} variant="outline" className="capitalize">
                        {type}
                      </Badge>
                    ))}
                    <Button variant="ghost" size="sm" onClick={() => openEdit(job)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(job)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedJob === job.id && (
                  <div className="mt-4 pt-4 border-t space-y-3">
                    {/* Rate Sheets */}
                    {job.rate_sheets && job.rate_sheets.length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-slate-500 flex items-center gap-1">
                          <DollarSign className="h-3 w-3" />
                          RATE SHEET
                        </span>
                        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
                          {job.rate_sheets.map((rate, idx) => (
                            <div key={idx} className="bg-slate-50 rounded-lg p-2 text-center">
                              <div className="text-xs text-slate-500">{rate.name}</div>
                              <div className="font-semibold text-blue-600">
                                ${rate.rate.toFixed(2)}
                                <span className="text-xs text-slate-400 ml-1">/{rate.uom}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {job.location && (
                      <div>
                        <span className="text-xs font-medium text-slate-500">LOCATION</span>
                        <p className="text-sm">{job.location}</p>
                      </div>
                    )}
                    {job.directions && (
                      <div>
                        <span className="text-xs font-medium text-slate-500">DIRECTIONS</span>
                        <p className="text-sm whitespace-pre-wrap">{job.directions}</p>
                      </div>
                    )}
                    {job.instructions && (
                      <div>
                        <span className="text-xs font-medium text-slate-500">INSTRUCTIONS</span>
                        <p className="text-sm whitespace-pre-wrap">{job.instructions}</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingJob ? 'Edit Job' : 'Add New Job'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Job Number & Client */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Job Number *</label>
                <Input
                  placeholder="e.g., 733"
                  value={formData.job_number}
                  onChange={(e) => setFormData({...formData, job_number: e.target.value})}
                  data-testid="job-number-input"
                />
                {!editingJob && formData.job_number && jobs.some(j => String(j.job_number).trim() === String(formData.job_number).trim()) && (
                  <p className="text-xs text-amber-400 mt-1" data-testid="job-number-duplicate-warning">
                    ⚠ Job #{formData.job_number} already exists. Save to add a product type to it, or pick a different number.
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Client Name *</label>
                <Select
                  value={formData.client_name || '_none_'}
                  onValueChange={(value) => {
                    if (value === '_none_') {
                      setFormData({...formData, client_name: ''});
                      return;
                    }
                    const c = clients.find(cc => cc.name === value);
                    if (c) {
                      // Compose the full address from the client's address fields
                      // (address, city, province, postal_code). Client may store the
                      // full address in `billing_address` or split across fields.
                      const composedAddress = c.billing_address
                        || [c.address, c.city, [c.province, c.postal_code].filter(Boolean).join(' ')]
                            .filter(s => s && s.trim()).join(', ');
                      setFormData(prev => ({
                        ...prev,
                        client_name: c.name,
                        // Auto-fill empty contact fields from client primary contact
                        contact_name: prev.contact_name || c.primary_contact_name || c.contact_name || '',
                        contact_phone: prev.contact_phone || c.primary_contact_phone || c.phone || '',
                        approval_email: prev.approval_email || c.approval_email || c.email || '',
                        billing_address: prev.billing_address || composedAddress || ''
                      }));
                    } else {
                      setFormData({...formData, client_name: value});
                    }
                  }}
                >
                  <SelectTrigger data-testid="client-name-select">
                    <SelectValue placeholder="Select existing client..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none_">-- Select Client --</SelectItem>
                    {clients.map(c => (
                      <SelectItem key={c.id || c.name} value={c.name}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {clients.length === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    No clients yet. Add one in the <Link to="/dashboard/clients" className="text-primary underline">Clients</Link> page first.
                  </p>
                )}
              </div>
            </div>

            {/* Contact Info */}
            {(() => {
              const selectedClient = clients.find(cc => cc.name === formData.client_name);
              const contactOptions = (selectedClient?.contacts || []).filter(c => c?.name);
              return (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Contact Name</label>
                  {contactOptions.length > 0 ? (
                    <Select
                      value={formData.contact_name || '_none_'}
                      onValueChange={(value) => {
                        if (value === '_none_') {
                          setFormData(prev => ({...prev, contact_name: '', contact_phone: ''}));
                          return;
                        }
                        // Custom (not in list) — just set the name, keep phone
                        if (value === '__manual__') {
                          return;
                        }
                        const ct = contactOptions.find(c => c.name === value);
                        if (ct) {
                          setFormData(prev => ({
                            ...prev,
                            contact_name: ct.name,
                            // Always overwrite phone when picking a contact (so phone matches)
                            contact_phone: ct.phone || prev.contact_phone || '',
                            approval_email: prev.approval_email || ct.email || '',
                          }));
                        }
                      }}
                    >
                      <SelectTrigger data-testid="contact-name-select">
                        <SelectValue placeholder="Select a contact..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none_">-- No Contact --</SelectItem>
                        {contactOptions.map(c => (
                          <SelectItem key={c.id || c.name} value={c.name}>
                            {c.name}{c.role ? ` — ${c.role}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      placeholder={selectedClient ? "No contacts saved — type one" : "e.g., Darryl Sinclair"}
                      value={formData.contact_name}
                      onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                      data-testid="contact-name-input"
                    />
                  )}
                  {selectedClient && contactOptions.length === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Add contacts to this client on the <Link to="/dashboard/clients" className="text-primary underline">Clients</Link> page to enable the dropdown.
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Contact Phone</label>
                  <Input
                    placeholder="e.g., (587) 228-9517"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                  />
                </div>
              </div>
              );
            })()}

            {/* Approval & Billing */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Approval Email</label>
                <Input
                  type="email"
                  placeholder="e.g., approvals@company.com"
                  value={formData.approval_email}
                  onChange={(e) => setFormData({...formData, approval_email: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Billing Address</label>
                <Input
                  placeholder="e.g., 123 Main St, City, AB T1A 1A1"
                  value={formData.billing_address}
                  onChange={(e) => setFormData({...formData, billing_address: e.target.value})}
                />
              </div>
            </div>

            {/* Site Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Site Name</label>
                <Input
                  placeholder="e.g., Jomax 3/Horizon 22"
                  value={formData.site_name}
                  onChange={(e) => setFormData({...formData, site_name: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Location (GPS/Legal)</label>
                <Input
                  placeholder="e.g., 5-30-77-17W6"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                />
              </div>
            </div>

            {/* Directions */}
            <div>
              <label className="text-sm font-medium mb-1 block">Directions</label>
              <Textarea
                placeholder="Enter driving directions..."
                value={formData.directions}
                onChange={(e) => setFormData({...formData, directions: e.target.value})}
                rows={3}
              />
            </div>

            {/* Instructions */}
            <div>
              <label className="text-sm font-medium mb-1 block">Special Instructions</label>
              <Textarea
                placeholder="Enter any special instructions..."
                value={formData.instructions}
                onChange={(e) => setFormData({...formData, instructions: e.target.value})}
                rows={3}
              />
            </div>

            {/* Job Types */}
            <div>
              <label className="text-sm font-medium mb-2 block">Job Types</label>
              <div className="flex gap-2">
                {JOB_TYPES.map(type => (
                  <Button
                    key={type}
                    type="button"
                    variant={formData.job_types.includes(type.toLowerCase()) ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleJobType(type)}
                  >
                    {formData.job_types.includes(type.toLowerCase()) && <Check className="h-3 w-3 mr-1" />}
                    {type}
                  </Button>
                ))}
              </div>
            </div>

            {/* Rate Sheets */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium flex items-center gap-1">
                  <DollarSign className="h-4 w-4" />
                  Rate Sheet
                </label>
                <div className="flex gap-2">
                  {formData.rate_sheets.length === 0 && (
                    <Button type="button" variant="outline" size="sm" onClick={addDefaultRates}>
                      Add Default Rates
                    </Button>
                  )}
                  <Button type="button" variant="outline" size="sm" onClick={addRate}>
                    <Plus className="h-3 w-3 mr-1" />
                    Add Rate
                  </Button>
                </div>
              </div>
              
              {formData.rate_sheets.length > 0 ? (
                <div className="space-y-2">
                  {/* Header */}
                  <div className="grid grid-cols-12 gap-2 text-xs font-medium text-slate-500 px-1">
                    <div className="col-span-4">Name</div>
                    <div className="col-span-3">Unit</div>
                    <div className="col-span-4">Rate ($)</div>
                    <div className="col-span-1"></div>
                  </div>
                  
                  {formData.rate_sheets.map((rate, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-4">
                        <Input
                          placeholder="Rate name"
                          value={rate.name}
                          onChange={(e) => updateRate(idx, 'name', e.target.value)}
                          className="h-9"
                        />
                      </div>
                      <div className="col-span-3">
                        <select
                          value={rate.uom}
                          onChange={(e) => updateRate(idx, 'uom', e.target.value)}
                          className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                        >
                          <option value="Tonne">Tonne</option>
                          <option value="Each">Each</option>
                          <option value="Yards">Yards</option>
                          <option value="Hours">Hours</option>
                          <option value="Loads">Loads</option>
                          <option value="Hour">Hour</option>
                          <option value="Load">Load</option>
                          <option value="KM">KM</option>
                          <option value="Day">Day</option>
                        </select>
                      </div>
                      <div className="col-span-4">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={rate.rate || ''}
                          onChange={(e) => updateRate(idx, 'rate', e.target.value)}
                          className="h-9"
                        />
                      </div>
                      <div className="col-span-1">
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removeRate(idx)}
                          className="h-9 w-9 p-0 text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">
                  No rates configured. Click "Add Default Rates" for quick setup.
                </p>
              )}
            </div>

            {/* Active Toggle */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="active"
                checked={formData.active}
                onChange={(e) => setFormData({...formData, active: e.target.checked})}
                className="rounded"
              />
              <label htmlFor="active" className="text-sm">Active (show in dispatch)</label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleSave} 
              disabled={saving || !formData.job_number || !formData.client_name}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingJob ? 'Save Changes' : 'Create Job'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Jobs Admin Dialog */}
      <Dialog open={showDupesDialog} onOpenChange={setShowDupesDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="duplicate-jobs-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Duplicate Job Records
            </DialogTitle>
          </DialogHeader>

          {dupesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            </div>
          ) : dupes.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <Check className="h-8 w-8 mx-auto mb-2 text-green-500" />
              No duplicate job numbers found.
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Each group below shares a job number. The record marked
                <Badge variant="outline" className="mx-1 text-green-600 border-green-600">Keep</Badge>
                is the most recently updated. Click <strong>Merge</strong> to repoint all linked
                assignments and tickets onto the keeper and delete the duplicate record(s).
              </p>

              {dupes.map((group) => {
                // First record is the suggested keeper (sorted newest-first server-side)
                const keeper = group.records[0];
                const losers = group.records.slice(1);
                const busyKey = `${group.job_number}:${keeper?.id}`;
                return (
                  <Card key={group.job_number} className="border-amber-500/30">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center justify-between">
                        <span>Job #{group.job_number}</span>
                        <Badge variant="outline" className="text-amber-500 border-amber-500">
                          {group.count} records
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {group.records.map((rec, idx) => (
                        <div
                          key={rec.id}
                          className={`rounded-md border p-3 text-sm ${
                            idx === 0
                              ? 'bg-green-500/10 border-green-500/30'
                              : 'bg-red-500/5 border-red-500/30'
                          }`}
                          data-testid={`dup-record-${rec.id}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <Badge
                              variant="outline"
                              className={idx === 0 ? 'text-green-600 border-green-600' : 'text-red-500 border-red-500'}
                            >
                              {idx === 0 ? 'Keep' : 'Delete'}
                            </Badge>
                            <span className="font-mono text-xs text-muted-foreground">{rec.id.slice(0, 8)}…</span>
                          </div>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                            <div><span className="text-muted-foreground">Client:</span> {rec.client_name || '—'}</div>
                            <div><span className="text-muted-foreground">Site:</span> {rec.site_name || '—'}</div>
                            <div><span className="text-muted-foreground">Contact:</span> {rec.contact_name || '—'}</div>
                            <div><span className="text-muted-foreground">Phone:</span> {rec.contact_phone || '—'}</div>
                            <div><span className="text-muted-foreground">Updated:</span> {rec.updated_at ? new Date(rec.updated_at).toLocaleString() : '—'}</div>
                            <div><span className="text-muted-foreground">Created:</span> {rec.created_at ? new Date(rec.created_at).toLocaleString() : '—'}</div>
                            <div><span className="text-muted-foreground">Active:</span> {rec.active === false ? 'No' : 'Yes'}</div>
                            <div><span className="text-muted-foreground">Linked:</span> {rec.assignment_count} asn · {rec.ticket_count} tkt</div>
                          </div>
                        </div>
                      ))}
                      <div className="flex justify-end pt-2">
                        {isAdmin ? (
                          <Button
                            size="sm"
                            disabled={mergeBusyKey === busyKey || losers.length === 0}
                            onClick={() =>
                              mergeDuplicateGroup(
                                group.job_number,
                                keeper.id,
                                losers.map(l => l.id)
                              )
                            }
                            data-testid={`merge-dup-${group.job_number}`}
                          >
                            {mergeBusyKey === busyKey ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <GitMerge className="h-4 w-4 mr-2" />
                            )}
                            Merge into keeper
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            Admin role required to merge.
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDupesDialog(false)} data-testid="close-dup-dialog">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
